import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchStrategiesForPeriod } from "../utils/restAPI";
import {
  setStrategiesForPeriod,
  setStrategyStoreLoading,
  setStrategyStoreError,
  switchPeriod,
  selectStrategiesForPeriod,
  selectStrategyStoreLoading,
  selectCurrentPeriod
} from "../Redux/Reducers/strategyStoreSlice";

const DEFAULT_PAGE_SIZE = 12;

const transformStrategyData = (data) =>
  data.map((strategy) => ({
    idStrategy: strategy.strategyId,
    title: strategy.strategyName,
    description: `Period Return: ${(((strategy.netNav ?? 0) * 100)).toFixed(2)}%`,
    chartData: (strategy.slices || []).map((slice) => ({
      date: slice.date,
      value: slice.netNav
    })),
    periodReturn: strategy.netNav ?? 0,
    sharpeRatio: strategy.sharpeRatio || 0 // handle null sharpe ratio value 
  }));

export const useStrategyStore = () => {
  const dispatch = useDispatch();
  const timePeriod = useSelector(selectCurrentPeriod);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Queue for serializing requests - response order matches request order
  const requestQueueRef = useRef([]);
  const isRequestPendingRef = useRef(false);

  // Keep track of last displayed strategies to show during loading
  const lastStrategiesRef = useRef([]);

    const strategiesFromStore = useSelector(selectStrategiesForPeriod(timePeriod));
  const loading = useSelector(selectStrategyStoreLoading);

  // Use last strategies while loading a new period, otherwise use current
  const strategies = useMemo(() => {
    if (strategiesFromStore) {
      lastStrategiesRef.current = strategiesFromStore;
      return strategiesFromStore;
    }
    // While loading, show previous strategies with blur effect
    return loading ? lastStrategiesRef.current : [];
  }, [strategiesFromStore, loading]);
  const [searchTerm, setSearchTerm] = useState("");

  const sendRequest = useCallback(async ({ period, page }) => {
    isRequestPendingRef.current = true;

    try {
      dispatch(setStrategyStoreError(null));
      dispatch(setStrategyStoreLoading(true));
      const response = await fetchStrategiesForPeriod(period, page, DEFAULT_PAGE_SIZE);

      if (response.success) {
        const transformed = transformStrategyData(response.data);
        dispatch(setStrategiesForPeriod({ period, strategies: transformed }));
        setPagination(response.pagination);
      } else {
        dispatch(setStrategyStoreError(response.message || "Failed to load strategies"));
      }
    } catch (error) {
      console.error(error);
      dispatch(setStrategyStoreError(error?.message || "Failed to load strategies"));
    } finally {
      dispatch(setStrategyStoreLoading(false));
      isRequestPendingRef.current = false;
    }
  }, [dispatch]);

  const processQueue = useCallback(async () => {
    if (isRequestPendingRef.current) return;
    const nextRequest = requestQueueRef.current.shift();
    if (!nextRequest) return;

    await sendRequest(nextRequest);

    if (requestQueueRef.current.length > 0) {
      processQueue();
    }
  }, [sendRequest]);

  const fetchStrategies = useCallback((period, page) => {
    requestQueueRef.current.push({ period, page });
    processQueue();
  }, [processQueue]);

  useEffect(() => {
    fetchStrategies(timePeriod, currentPage);
  }, [timePeriod, currentPage, fetchStrategies]);

  const handleTimePeriodChange = useCallback((_, newPeriod) => {
    if (newPeriod !== null) {
      dispatch(switchPeriod(newPeriod));
    }
  }, [dispatch]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const filteredStrategies = useMemo(() =>
    strategies.filter((strategy) =>
      strategy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      strategy.description.toLowerCase().includes(searchTerm.toLowerCase())
    ), [strategies, searchTerm]);

  return {
    loading,
    timePeriod,
    searchTerm,
    setSearchTerm,
    strategies,
    filteredStrategies,
    handleTimePeriodChange,
    pagination,
    currentPage,
    handlePageChange
  };
};
